import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CheckRounded,
  CurrencyRupeeOutlined,
  ErrorOutlineOutlined,
  EventOutlined,
  FunctionsOutlined,
  PendingActionsOutlined,
  ReceiptLongOutlined,
  TagOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { esiReconciliationHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { PayInputAuditTrail } from "pages/PayRoll/PayInputs/Shared/AuditTrail";
import { Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { MilestoneStepper, PlainInput, StatCardGrid, StatutoryList } from "pages/PayRoll/Shared/statutoryUi";
import { FilePick, fileError } from "pages/PayRoll/Statutory/PF/PfMemberPanels";
import { CODES, RECON_STATUS_COLORS, codeName, codeRows, dueDate, formatDay, liabilityOf, money, monthLabel, num, reconLabel, reconStatus } from "./esiReportsData";

// Reconciliation (R4), Record Challan (R4a) and the challan view: one row
// per ESI code per month. Liability comes from the finalised payroll; the
// status is worked out from the total of all challans for the code and
// month (Not recorded / Partly paid / Paid / Overpaid). Challans are never
// overwritten: edits need a reason and are kept in the history.

const { indigo, green, purple, orange, pink } = detailAccentPalette;
const MONTHS = ["2026-09", "2026-08"];
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };

const ChallanPanel = ({ open, row, editing, allChallans, onClose, onSave }) => {
  const [form, setForm] = useState(null);
  const [file, setFile] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open || !row) return;
    setForm(
      editing
        ? { amount: String(editing.amount), paid_on: editing.paid_on, reference: editing.reference, remarks: editing.remarks || "", receipt: editing.receipt, reason: "" }
        : { amount: String(Math.max(0, row.liability.total - row.paid)), paid_on: "", reference: "", remarks: "", receipt: "", reason: "" },
    );
    setFile(null);
    setShowErrors(false);
  }, [open, row, editing]);

  if (!row || !form) return null;
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const amount = Number(form.amount) || 0;
  const otherPaid = row.paid - (editing ? editing.amount : 0);
  const statusAfter = reconStatus(row.liability.total, otherPaid + amount);
  const kind = editing ? editing.kind : row.challans.length ? "Supplementary" : "Original";

  const errors = {};
  if (!form.receipt) errors.receipt = "Upload the challan or receipt";
  else if (fileError(file)) errors.receipt = fileError(file);
  if (!(amount > 0) || !Number.isInteger(amount)) errors.amount = "Whole rupees, more than 0";
  if (!form.paid_on) errors.paid_on = "Pick the paid date";
  const reference = form.reference.trim();
  if (!reference) errors.reference = "Enter the challan reference / transaction no.";
  else if (allChallans.some((challan) => challan.id !== editing?.id && challan.reference.toLowerCase() === reference.toLowerCase())) errors.reference = "This reference is already recorded";
  if (editing && form.reason.trim().length < 5) errors.reason = "Give the reason for the change (audited)";
  const error = (key) => (showErrors ? errors[key] : undefined);

  const save = () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    onSave({ ...form, amount, reference, remarks: form.remarks.trim(), reason: form.reason.trim(), kind });
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={editing ? "Edit Challan" : kind === "Supplementary" ? "Add Supplementary Challan" : "Record Challan"}
      subtitle={`${row.month_label} · ${codeName(row.code)} · ${row.code.code}`}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save Challan" primaryProps={{ onClick: save }} />}
    >
      <Mui.Stack spacing={2}>
        <RequestDetailsSection
          title="LIABILITY (FROM REGISTER)"
          fields={[
            {
              icon: FunctionsOutlined,
              label: "Total payable",
              value: money(row.liability.total),
              helper: `Employee ${money(row.liability.employee)} + employer ${money(row.liability.employer)} · ${num(row.liability.count)} employees`,
              accent: indigo,
            },
            ...(row.paid && !editing ? [{ icon: CheckRounded, label: "Already paid", value: money(row.paid), helper: `Balance ${money(row.liability.total - row.paid)}`, accent: green }] : []),
            { icon: EventOutlined, label: "Due date", value: formatDay(dueDate(row.month)), accent: orange },
          ]}
        />
        <FormSection title={<>Challan / Receipt<Required /></>}>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <FilePick
              label={form.receipt && !file ? form.receipt : "Upload challan / receipt"}
              hint="PDF, JPG or PNG · max 5 MB"
              value={file}
              error={error("receipt")}
              onChange={(picked) => {
                setFile(picked);
                set("receipt")(picked.name);
              }}
            />
          </Mui.Box>
        </FormSection>
        <FormSection title="Challan Details">
          <Field label={<>Challan amount (₹)<Required /></>} error={error("amount")}>
            <PlainInput type="number" value={form.amount} onChange={set("amount")} error={error("amount")} inputProps={{ min: 1, step: 1 }} />
          </Field>
          <Field label={<>Paid on<Required /></>} error={error("paid_on")}>
            <DateField value={form.paid_on} onChange={set("paid_on")} maxDate={moment().format("YYYY-MM-DD")} error={error("paid_on")} />
          </Field>
          <Field wide label={<>Challan reference / transaction no.<Required /></>} error={error("reference")}>
            <TextField value={form.reference} onChange={set("reference")} error={error("reference")} placeholder="e.g. ESIC-CH-99542" />
          </Field>
          <Field wide label="Remarks (optional)">
            <TextField value={form.remarks} onChange={set("remarks")} placeholder="—" />
          </Field>
          {editing ? (
            <Field wide label={<>Reason for the change<Required /></>} error={error("reason")}>
              <TextField value={form.reason} onChange={set("reason")} error={error("reason")} placeholder="e.g. Amount typed wrongly" />
            </Field>
          ) : null}
          <Mui.Typography
            sx={{ ...designType.captionText, gridColumn: "1 / -1", fontWeight: designWeights.semibold, color: statusAfter === "Paid" ? "#067647" : "#B54708" }}
          >
            {statusAfter === "Paid"
              ? "✓ Amount matches liability — status will be Paid"
              : `${reconLabel(statusAfter, row.liability.total, otherPaid + amount)} — status will be ${statusAfter}`}
          </Mui.Typography>
        </FormSection>
        <RequestInfoBanner body="If the amount differs, the status shows the difference and stays open until a supplementary challan is recorded. Each challan keeps its own receipt; none replaces another." />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const ChallanViewPanel = ({ open, row, fileGenerated, onClose, onEdit, onSupplementary }) => {
  if (!row) return null;
  const latest = row.challans[row.challans.length - 1];
  const history = row.challans.flatMap((challan) => challan.history || []);
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="ESI Challan"
      subtitle={`${row.month_label} · ${codeName(row.code)} · ${row.code.code}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          {latest ? <SecondaryButton onClick={() => onEdit(latest)}>Edit</SecondaryButton> : null}
          <SecondaryButton onClick={onSupplementary}>Add supplementary challan</SecondaryButton>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RequestDetailsSection
          title="CHALLAN DETAILS"
          fields={[
            {
              icon: FunctionsOutlined,
              label: "Liability (from register)",
              value: money(row.liability.total),
              helper: `Employee ${money(row.liability.employee)} + employer ${money(row.liability.employer)}`,
              accent: indigo,
            },
            ...row.challans.flatMap((challan) => [
              { icon: CurrencyRupeeOutlined, label: `${challan.kind} challan amount`, value: money(challan.amount), accent: green },
              { icon: TagOutlined, label: "Challan reference", value: challan.reference, accent: purple },
              { icon: EventOutlined, label: "Paid on", value: `${formatDay(challan.paid_on)} · due ${formatDay(dueDate(row.month))}`, accent: orange },
              { icon: ReceiptLongOutlined, label: "Receipt", value: challan.receipt, helper: "View · Download", accent: pink },
            ]),
          ]}
        />
        <MilestoneStepper
          title="Status"
          steps={[
            { label: "File generated", date: fileGenerated ? formatDay(fileGenerated) : "", done: Boolean(fileGenerated) },
            { label: "Paid", date: latest ? formatDay(latest.paid_on) : "", done: row.paid > 0 },
            { label: "Reconciled", date: row.status === "Paid" ? "Amounts match" : "", done: row.status === "Paid" },
          ]}
        />
        {row.status !== "Paid" ? (
          <RequestInfoBanner tone="warning" body={`${reconLabel(row.status, row.liability.total, row.paid)}. ${row.status === "Overpaid" ? "Correct the challan with Edit." : "Add a supplementary challan for the balance."}`} />
        ) : null}
        {history.length ? (
          <Mui.Box>
            <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1 }}>Changes</Mui.Typography>
            <PayInputAuditTrail entries={history} />
          </Mui.Box>
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const EsiReconciliationTab = ({ month, codeFilter, challans, files, onRecord, onEdit }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [card, setCard] = useState("all");
  const [panel, setPanel] = useState({ kind: null, key: null, challan: null });
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "esi_reconciliation_saved_views",
    fullHeaders: esiReconciliationHeader,
  });

  const allRows = useMemo(
    () =>
      MONTHS.flatMap((forMonth) =>
        CODES.filter((code) => codeFilter === "all" || code.code === codeFilter).map((code) => {
          const liability = liabilityOf(codeRows(forMonth, code));
          const own = challans.filter((challan) => challan.code === code.code && challan.month === forMonth);
          const paid = own.reduce((sum, challan) => sum + challan.amount, 0);
          const status = reconStatus(liability.total, paid);
          const latest = own[own.length - 1];
          return {
            id: `${forMonth}-${code.code}`,
            month: forMonth,
            month_label: monthLabel(forMonth),
            code,
            code_label: codeName(code),
            liability,
            liability_total: liability.total,
            paid,
            paid_on: latest?.paid_on || "",
            reference: own.map((challan) => challan.reference).join(", "),
            status,
            action: status === "Not recorded" ? "Record challan" : "View",
            challans: own,
          };
        }),
      ),
    [challans, codeFilter],
  );

  const row = allRows.find((item) => item.id === panel.key) || null;
  const current = allRows.filter((item) => item.month === month);
  const rows =
    card === "paid"
      ? allRows.filter((item) => item.status === "Paid")
      : card === "none"
        ? allRows.filter((item) => item.status === "Not recorded")
        : card === "diff"
          ? allRows.filter((item) => item.status === "Partly paid" || item.status === "Overpaid")
          : allRows;
  const short = monthLabel(month).split(" ")[0];

  const renderCell = (item, key) => {
    switch (key) {
      case "liability_total":
        return num(item.liability.total);
      case "paid":
        return item.paid ? num(item.paid) : "—";
      case "paid_on":
        return item.paid_on ? formatDay(item.paid_on) : "—";
      case "status":
        return <RequestStatusChip status={reconLabel(item.status, item.liability.total, item.paid)} colorCode={RECON_STATUS_COLORS[item.status]} />;
      case "action":
        return <Mui.Box component="span" sx={linkSx}>{item.action}</Mui.Box>;
      default:
        return item[key] || "—";
    }
  };

  const close = () => setPanel({ kind: null, key: null, challan: null });

  return (
    <>
      <StatCardGrid
        value={card}
        onChange={setCard}
        cards={[
          { key: "all", title: `Liability (${short})`, value: money(current.reduce((sum, item) => sum + item.liability.total, 0)), icon: FunctionsOutlined, tone: "blue" },
          { key: "paid", title: "Paid", value: money(current.reduce((sum, item) => sum + item.paid, 0)), icon: CheckRounded, tone: "green" },
          { key: "none", title: "Not recorded", value: money(current.filter((item) => item.status === "Not recorded").reduce((sum, item) => sum + item.liability.total, 0)), icon: PendingActionsOutlined, tone: "amber" },
          { key: "diff", title: "Differences", value: allRows.filter((item) => item.status === "Partly paid" || item.status === "Overpaid").length, icon: ErrorOutlineOutlined, tone: "red" },
        ]}
      />
      <StatutoryList
        title="ESI Reconciliation"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(item) => setPanel({ kind: item.action === "View" ? "view" : "record", key: item.id, challan: null })}
        selectedKey={panel.key}
        itemLabel="rows"
        searchPlaceholder="Search code or reference"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Stack spacing={1.5} sx={{ mt: 1.5 }}>
        <Mui.Typography sx={designType.captionText}>Due date: 15th of the next month ({short} challan due {formatDay(dueDate(month))})</Mui.Typography>
        <RequestInfoBanner body="After paying on the ESIC portal, record the challan here with its receipt. Status is worked out from all challans for the code and month: Not recorded · Partly paid · Paid · Overpaid. A month is Reconciled when its status is Paid." />
      </Mui.Stack>

      <ChallanPanel
        open={panel.kind === "record" || panel.kind === "edit"}
        row={row}
        editing={panel.kind === "edit" ? panel.challan : null}
        allChallans={challans}
        onClose={close}
        onSave={(form) => {
          if (panel.kind === "edit") {
            onEdit(panel.challan.id, form);
            showToast("success", `Challan ${form.reference} updated.`);
          } else {
            onRecord({ code: row.code.code, month: row.month, ...form });
            showToast("success", `Challan ${form.reference} recorded.`);
          }
          setPanel({ kind: "view", key: panel.key, challan: null });
        }}
      />
      <ChallanViewPanel
        open={panel.kind === "view"}
        row={row}
        fileGenerated={row ? files.find((file) => file.code === row.code.code && file.month === row.month && file.status === "Current")?.generated_at : null}
        onClose={close}
        onEdit={(challan) => setPanel({ kind: "edit", key: panel.key, challan })}
        onSupplementary={() => setPanel({ kind: "record", key: panel.key, challan: null })}
      />
    </>
  );
};
